import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { getConsent, setConsent } from '@/lib/consent'
import { initializeTelemetry } from '@/platform/telemetry'

/**
 * Cookie / analytics consent banner. Renders only when the user has not yet
 * made a choice. On "Accept", flips consent to granted and (re-)initialises
 * Sentry. On "Reject", flips consent to denied and Sentry stays uninitialised.
 *
 * Choice is persisted in localStorage; the banner won't reappear unless the
 * user clears storage or revokes consent from the Settings page (future).
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setVisible(getConsent() === 'undecided')
  }, [])

  if (!visible) return null

  const accept = () => {
    setConsent('granted')
    setVisible(false)
    void initializeTelemetry()
  }

  const reject = () => {
    setConsent('denied')
    setVisible(false)
  }

  return createPortal(
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie and analytics consent"
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:left-auto sm:max-w-md z-50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 shadow-2xl">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
          We use cookies to keep CircleUp reliable
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
          Essential cookies keep you signed in and process payments. Optional
          analytics (Sentry crash reports) help us fix bugs faster — they're off
          until you opt in. See our{' '}
          <a href="/privacy" className="underline text-indigo-600 dark:text-indigo-400 hover:text-indigo-700">
            privacy policy
          </a>{' '}
          for details.
        </p>
        <div className="flex gap-2">
          <button
            onClick={accept}
            className="flex-1 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition"
          >
            Accept all
          </button>
          <button
            onClick={reject}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
